class HorizontalCarouselIcon extends HTMLElement {
	constructor() {
		super();
		this.scrollContainer = null;
		this.leftButton = null;
		this.rightButton = null;
		this.isScrolling = false;
		this.currentIndex = 0; // 当前居中卡片索引
		this.resizeObserver = null;
		this.isMobile = false;
	}

	connectedCallback() {
		this.checkMobile();
		this.render();
		this.loadData();
		this.setupEventListeners();
		this.setupResizeObserver();
		this.handleResize();
	}

	disconnectedCallback() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	}

	checkMobile() {
		this.isMobile = window.innerWidth < 1024; // lg断点
	}

	handleResize() {
		window.addEventListener('resize', () => {
			const wasMobile = this.isMobile;
			this.checkMobile();
			if (wasMobile !== this.isMobile) {
				this.render();
				this.loadData();
				this.setupEventListeners();
			}
		});
	}

	get data() {
		const dataAttr = this.getAttribute('data');
		if (dataAttr) {
			try {
				return JSON.parse(decodeURIComponent(dataAttr));
			} catch (e) {
				console.error('Invalid data attribute:', e);
			}
		}
		return [];
	}

	render() {
		if (this.isMobile) {
			this.renderMobile();
		} else {
			this.renderDesktop();
		}
	}

	renderMobile() {
		this.innerHTML = `
			<div class="relative w-full max-w-[1200px] mx-auto">
				<div class="overflow-hidden relative">
					<div class="car-icon-scroll-container flex gap-4 transition-transform duration-300 ease-out" style="transform: translateX(0px);">
					</div>
				</div>

				<!-- 移动端分页导航点 -->
				<div class="car-icon-pagination-dots flex justify-center mt-6 space-x-3" id="pagination-container">
				</div>
			</div>

			<style>
				.car-icon-pagination-dot {
					cursor: pointer;
					transition: all 0.3s ease;
					transform-origin: center;
				}

				.car-icon-pagination-dot:hover {
					transform: scale(1.2);
				}

				.car-icon-pagination-dot.active {
					background: linear-gradient(45deg, #6366f1, #8b5cf6);
					transform: scale(1.3);
					box-shadow: 0 0 12px rgba(139, 92, 246, 0.7);
				}

				/* 移动端卡片样式优化 */
				@media (max-width: 1023px) {
					.car-icon-feature-card {
						width: 96% !important;
						flex-shrink: 0;
					}

					.car-icon-scroll-container {
						padding-left: 0.5rem;
						padding-right: 0.5rem;
					}
				}
			</style>
		`;

		this.scrollContainer = this.querySelector('.car-icon-scroll-container');
	}

	renderDesktop() {
		this.innerHTML = `
			<div class="relative w-full max-w-[1200px] mx-auto">
				<div class="overflow-hidden relative">
					<div class="car-icon-scroll-container flex gap-6 transition-transform duration-300 ease-out" style="transform: translateX(0px);">
					</div>
				</div>

				<div class="flex justify-center mt-8 gap-4">
					<button class="car-icon-nav-button left-button w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors duration-200">
						<svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
						</svg>
					</button>
					<button class="car-icon-nav-button right-button w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors duration-200">
						<svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
						</svg>
					</button>
				</div>
			</div>
		`;

		this.scrollContainer = this.querySelector('.car-icon-scroll-container');
		this.leftButton = this.querySelector('.left-button');
		this.rightButton = this.querySelector('.right-button');
	}

	loadData() {
		const data = this.data;
		this.renderCards(data);

		if (this.isMobile) {
			this.renderPaginationDots(data.length);
			// 在移动端渲染完成后统一卡片高度
			this.equalizeCardHeights();
		}

		// 渲染完成后更新导航状态
		setTimeout(() => {
			this.updateNavigationState();
		}, 0);
	}

	renderCards(features) {
		const cardWidth = this.isMobile ? 'w-[96%]' : 'w-[36rem]';

		const cardsHTML = features
			.map(
				(feature, index) => `
				<div class="car-icon-feature-card flex-shrink-0 ${cardWidth} overflow-hidden bg-[#131313] rounded-lg pt-4 pb-10 transition-colors duration-300 relative" data-index="${index}">
					<div class="pt-6 pl-6 z-10 w-16 h-16 flex items-center justify-center">
						${
							feature.icon
								? `<img src="${feature.icon}" alt="Icon" class="w-16 h-16 object-contain" />`
								: ''
						}
					</div>
					<div class="pl-6 mt-4">
						<h3 class="text-2xl lg:text-4xl font-semibold text-white mb-3 leading-tight">
							${feature.title}
						</h3>
						<p class="text-white text-2xl leading-relaxed">
							${feature.description}
						</p>
					</div>
				</div>
			`
			)
			.join('');

		if (cardsHTML && this.scrollContainer) {
			this.scrollContainer.innerHTML = cardsHTML;
		}

		// 在卡片内容渲染完成后，确保所有图片加载完成再统一高度
		if (this.isMobile) {
			const images = this.scrollContainer.querySelectorAll('img');
			if (images.length > 0) {
				Promise.all(
					Array.from(images).map((img) => {
						if (img.complete) return Promise.resolve();
						return new Promise((resolve) => {
							img.onload = resolve;
							img.onerror = resolve;
						});
					})
				).then(() => {
					this.equalizeCardHeights();
				});
			} else {
				this.equalizeCardHeights();
			}
		}
	}

	renderPaginationDots(count) {
		const paginationContainer = this.querySelector('#pagination-container');
		if (!paginationContainer) return;

		const dotsHTML = Array.from(
			{length: count},
			(_, index) => `
			<button class="car-icon-pagination-dot w-3 h-3 rounded-full transition-all duration-300 ${
				index === 0 ? 'active' : 'bg-gray-600'
			}" data-index="${index}"></button>
		`
		).join('');

		paginationContainer.innerHTML = dotsHTML;

		// 渲染完成后立即绑定分页点事件
		this.bindPaginationEvents();
	}

	bindPaginationEvents() {
		const paginationDots = this.querySelectorAll('.car-icon-pagination-dot');
		paginationDots.forEach((dot) => {
			dot.addEventListener('click', this.handlePaginationClick.bind(this));
		});
	}

	setupEventListeners() {
		if (this.isMobile) {
			this.setupMobileEventListeners();
		} else {
			this.setupDesktopEventListeners();
		}
	}

	setupMobileEventListeners() {
		// 触摸滑动支持
		const container = this.scrollContainer.parentElement;
		if (container) {
			this.addTouchSupport(container);
		}
	}

	setupDesktopEventListeners() {
		this.addEventListener('wheel', this.handleWheel.bind(this), {
			passive: false
		});
		this.leftButton.addEventListener('click', () => this.scrollLeft());
		this.rightButton.addEventListener('click', () => this.scrollRight());
		this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
		this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
	}

	addTouchSupport(container) {
		let startX = 0;
		let startY = 0;
		let isDown = false;
		let startTransform = 0;

		container.addEventListener(
			'touchstart',
			(e) => {
				if (this.isScrolling) return;

				isDown = true;
				startX = e.touches[0].clientX;
				startY = e.touches[0].clientY;
				startTransform = this.getCurrentTransform();

				// 暂停当前动画
				this.scrollContainer.style.transition = 'none';
			},
			{passive: true}
		);

		container.addEventListener(
			'touchmove',
			(e) => {
				if (!isDown) return;

				const currentX = e.touches[0].clientX;
				const currentY = e.touches[0].clientY;
				const diffX = Math.abs(currentX - startX);
				const diffY = Math.abs(currentY - startY);

				// 如果是水平滑动（水平距离大于垂直距离），阻止默认滚动
				if (diffX > diffY && diffX > 10) {
					e.preventDefault();

					// 实时跟随手指移动，但限制移动范围
					const deltaX = currentX - startX;
					const newTransform = startTransform + deltaX;
					const maxScroll = this.getMaxScroll();
					const clampedTransform = Math.max(
						Math.min(newTransform, 0),
						maxScroll
					);

					this.scrollContainer.style.transform = `translateX(${clampedTransform}px)`;

					// 实时更新分页点状态
					this.updatePaginationDotsFromTransform(clampedTransform);
				}
			},
			{passive: false}
		);

		container.addEventListener(
			'touchend',
			(e) => {
				if (!isDown) return;
				isDown = false;

				const endX = e.changedTouches[0].clientX;
				const diffX = startX - endX;
				const threshold = 50; // 最小滑动距离

				// 恢复动画
				this.scrollContainer.style.transition = 'transform 0.3s ease-out';

				if (Math.abs(diffX) > threshold) {
					// 根据滑动方向决定目标索引
					const currentIndex = this.getCurrentCardIndex();
					let targetIndex;

					if (diffX > 0) {
						// 向左滑动，下一张
						targetIndex = Math.min(
							currentIndex + 1,
							this.scrollContainer.children.length - 1
						);
					} else {
						// 向右滑动，上一张
						targetIndex = Math.max(currentIndex - 1, 0);
					}

					this.scrollToIndex(targetIndex);
					this.updatePaginationDots(targetIndex);
				} else {
					// 滑动距离不够，回弹到当前位置
					const currentIndex = this.getCurrentCardIndex();
					this.scrollToIndex(currentIndex);
					this.updatePaginationDots(currentIndex);
				}
			},
			{passive: true}
		);
	}

	updatePaginationDotsFromTransform(transform) {
		if (!this.isMobile) return;

		// 根据当前 transform 计算最接近的卡片索引
		const containerWidth = this.getContainerWidth();
		const cardWidth = containerWidth * 0.96; // 96% 宽度
		const cardGap = this.getCardGap();
		const cardWithGap = cardWidth + cardGap;

		const index = Math.round(-transform / cardWithGap);
		const clampedIndex = Math.max(
			0,
			Math.min(index, this.scrollContainer.children.length - 1)
		);

		const dots = this.querySelectorAll('.car-icon-pagination-dot');
		dots.forEach((dot, i) => {
			if (i === clampedIndex) {
				dot.classList.add('active');
				dot.style.background = 'linear-gradient(45deg, #6366f1, #8b5cf6)';
				dot.classList.remove('bg-gray-600');
			} else {
				dot.classList.remove('active');
				dot.style.background = '';
				dot.classList.add('bg-gray-600');
			}
		});
	}

	handlePaginationClick(e) {
		if (this.isScrolling) return;
		const index = parseInt(e.currentTarget.dataset.index);
		if (!isNaN(index)) {
			this.scrollToIndex(index);
			this.updatePaginationDots(index);
		}
	}

	updatePaginationDots(activeIndex = null) {
		if (!this.isMobile) return;

		const currentIndex =
			activeIndex !== null ? activeIndex : this.getCurrentCardIndex();
		const dots = this.querySelectorAll('.car-icon-pagination-dot');

		dots.forEach((dot, index) => {
			if (index === currentIndex) {
				dot.classList.add('active');
				dot.style.background = 'linear-gradient(45deg, #6366f1, #8b5cf6)';
				dot.classList.remove('bg-gray-600');
			} else {
				dot.classList.remove('active');
				dot.style.background = '';
				dot.classList.add('bg-gray-600');
			}
		});
	}

	equalizeCardHeights() {
		if (!this.isMobile || !this.scrollContainer) return;

		// 重置所有卡片高度，以便获取真实高度
		const cards = this.scrollContainer.querySelectorAll(
			'.car-icon-feature-card'
		);
		cards.forEach((card) => {
			card.style.height = 'auto';
		});

		// 计算最大高度
		let maxHeight = 0;
		cards.forEach((card) => {
			const height = card.offsetHeight;
			maxHeight = Math.max(maxHeight, height);
		});

		// 应用最大高度到所有卡片
		if (maxHeight > 0) {
			cards.forEach((card) => {
				card.style.height = `${maxHeight}px`;
			});
		}
	}

	setupResizeObserver() {
		this.resizeObserver = new ResizeObserver(() => {
			this.updateNavigationState();
			// 在窗口大小改变时也需要重新计算卡片高度
			if (this.isMobile) {
				this.equalizeCardHeights();
			}
		});
		this.resizeObserver.observe(this);
	}

	getCardWidth() {
		if (this.scrollContainer && this.scrollContainer.children.length > 0) {
			const firstCard = this.scrollContainer.children[0];
			return firstCard.offsetWidth;
		}
		return 0;
	}

	getCardGap() {
		if (this.scrollContainer && this.scrollContainer.children.length > 1) {
			const firstCard = this.scrollContainer.children[0];
			const secondCard = this.scrollContainer.children[1];
			const firstCardRight = firstCard.offsetLeft + firstCard.offsetWidth;
			const secondCardLeft = secondCard.offsetLeft;
			return secondCardLeft - firstCardRight;
		}
		return 0;
	}

	getContainerWidth() {
		const container = this.scrollContainer.parentElement;
		const computedStyle = window.getComputedStyle(container);
		const paddingLeft = parseFloat(computedStyle.paddingLeft);
		const paddingRight = parseFloat(computedStyle.paddingRight);
		return container.offsetWidth - paddingLeft - paddingRight;
	}

	updateNavigationState() {
		if (!this.scrollContainer || this.scrollContainer.children.length === 0) {
			return;
		}

		if (this.isMobile) {
			// 移动端总是显示分页导航点
			const paginationContainer = this.querySelector('#pagination-container');
			if (paginationContainer && this.scrollContainer.children.length > 1) {
				paginationContainer.style.display = 'flex';
			} else if (paginationContainer) {
				paginationContainer.style.display = 'none';
			}
			this.updatePaginationDots();
		} else {
			// 桌面端逻辑保持不变
			const containerWidth = this.getContainerWidth();
			const cardCount = this.scrollContainer.children.length;
			const cardWidth = this.getCardWidth();
			const cardGap = this.getCardGap();
			const totalCardsWidth = cardCount * cardWidth + (cardCount - 1) * cardGap;

			const hasEnoughSpace = containerWidth >= totalCardsWidth;

			// 显示或隐藏导航按钮
			const navigationContainer = this.querySelector(
				'.flex.justify-center.mt-8'
			);
			if (navigationContainer) {
				navigationContainer.style.display = hasEnoughSpace ? 'none' : 'flex';
			}

			if (hasEnoughSpace) {
				// 居中显示卡片
				this.centerCards();
			} else {
				// 重置到默认位置
				this.scrollContainer.style.transform = 'translateX(0px)';
				this.scrollContainer.style.justifyContent = 'flex-start';
			}
		}
	}

	centerCards() {
		// 移除transform，使用justify-center来居中
		this.scrollContainer.style.transform = 'translateX(0px)';
		this.scrollContainer.style.justifyContent = 'center';
	}

	getMaxScroll() {
		const containerWidth = this.getContainerWidth();
		const scrollWidth = this.scrollContainer.scrollWidth;
		return Math.min(0, containerWidth - scrollWidth);
	}

	handleWheel(event) {
		const containerWidth = this.getContainerWidth();
		const scrollWidth = this.scrollContainer.scrollWidth;

		// 如果内容宽度小于或等于容器宽度，允许垂直滚动
		if (scrollWidth <= containerWidth) {
			return;
		}

		event.preventDefault();

		const delta = event.deltaY;
		const scrollSensitivity = 1;
		const currentTransform = this.getCurrentTransform();
		const scrollAmount = delta * scrollSensitivity;

		let newTransform = currentTransform - scrollAmount;
		const maxScroll = this.getMaxScroll();
		newTransform = Math.max(Math.min(newTransform, 0), maxScroll);

		this.scrollContainer.style.transform = `translateX(${newTransform}px)`;
		// 重置justify-content以确保滚动正常工作
		this.scrollContainer.style.justifyContent = 'flex-start';
	}

	getCurrentCardIndex() {
		if (this.isMobile) {
			// 移动端计算当前卡片索引
			const currentTransform = this.getCurrentTransform();
			const containerWidth = this.getContainerWidth();
			const cardWidth = containerWidth * 0.96; // 96% 宽度
			const cardGap = this.getCardGap();
			const cardWithGap = cardWidth + cardGap;

			const index = Math.round(-currentTransform / cardWithGap);
			return Math.max(
				0,
				Math.min(index, this.scrollContainer.children.length - 1)
			);
		} else {
			// 桌面端逻辑保持不变
			const currentTransform = this.getCurrentTransform();
			const containerWidth = this.getContainerWidth();
			const cardWidth = this.getCardWidth();
			const cardGap = this.getCardGap();
			const cardWithGap = cardWidth + cardGap;
			const cardCenterOffset = (containerWidth - cardWidth) / 2;

			const index = Math.round(
				(-currentTransform + cardCenterOffset) / cardWithGap
			);
			return Math.max(
				0,
				Math.min(index, this.scrollContainer.children.length - 1)
			);
		}
	}

	scrollLeft() {
		if (this.isScrolling) return;

		// 重置justify-content以确保滚动正常工作
		this.scrollContainer.style.justifyContent = 'flex-start';

		const currentIndex = this.getCurrentCardIndex();
		const targetIndex = Math.max(currentIndex - 1, 0);
		this.scrollToIndex(targetIndex);

		if (this.isMobile) {
			this.updatePaginationDots(targetIndex);
		}
	}

	scrollRight() {
		if (this.isScrolling) return;

		// 重置justify-content以确保滚动正常工作
		this.scrollContainer.style.justifyContent = 'flex-start';

		const currentIndex = this.getCurrentCardIndex();
		const maxIndex = this.scrollContainer.children.length - 1;
		const targetIndex = Math.min(currentIndex + 1, maxIndex);
		this.scrollToIndex(targetIndex);

		if (this.isMobile) {
			this.updatePaginationDots(targetIndex);
		}
	}

	scrollToIndex(index) {
		if (this.isMobile) {
			// 移动端：考虑96%宽度的卡片滚动
			const containerWidth = this.getContainerWidth();
			const cardWidth = containerWidth * 0.96; // 96% 宽度
			const cardGap = this.getCardGap();
			const cardWithGap = cardWidth + cardGap;
			const targetTransform = -index * cardWithGap;

			const maxScroll = this.getMaxScroll();
			const finalTransform = Math.max(Math.min(targetTransform, 0), maxScroll);

			this.animateScroll(finalTransform);
		} else {
			// 桌面端逻辑保持不变
			const containerWidth = this.getContainerWidth();
			const cardWidth = this.getCardWidth();
			const cardGap = this.getCardGap();
			const cardWithGap = cardWidth + cardGap;
			const cardCenterOffset = (containerWidth - cardWidth) / 2;
			const targetTransform = -index * cardWithGap + cardCenterOffset;

			const maxScroll = this.getMaxScroll();
			const finalTransform = Math.max(Math.min(targetTransform, 0), maxScroll);

			this.animateScroll(finalTransform);
		}
	}

	getCurrentTransform() {
		const transform = this.scrollContainer.style.transform;
		const match = transform.match(/translateX\((-?\d+(?:\.\d+)?)px\)/);
		return match ? parseFloat(match[1]) : 0;
	}

	animateScroll(targetX) {
		this.isScrolling = true;
		this.scrollContainer.style.transition = 'transform 0.3s ease-out';
		this.scrollContainer.style.transform = `translateX(${targetX}px)`;

		setTimeout(() => {
			this.isScrolling = false;
			this.scrollContainer.style.transition = '';
			// 动画完成后更新分页点状态
			if (this.isMobile) {
				this.updatePaginationDots();
			}
		}, 300);
	}

	handleMouseEnter() {}

	handleMouseLeave() {
		this.style.cursor = 'default';
	}

	static get observedAttributes() {
		return ['data'];
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (name === 'data' && oldValue !== newValue) {
			this.loadData();
		}
	}
}

customElements.define('horizontal-carousel-icon', HorizontalCarouselIcon);
